import { F } from '@/utils/intl';
import React, { useEffect, useState } from 'react';
import { useDispatch, useIntl, useModel, useSelector } from 'umi';
import { Button, Empty, Form, Grid, Input, message, Modal, Space, Spin, Tabs, Typography } from 'antd';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';
import classNames from 'classnames';
import ProForm, { ProFormText, ProFormTextArea } from '@ant-design/pro-form';
import ToggleCard from '@/components/ToggleCard/ToggleCard';

import * as ServiceRequestSelectors from '@/selectors/serviceRequestSelectors';
import * as LeadSelectors from '@/selectors/leadSelectors';
import LeadsSelect from '@/components/Filters/LeadsSelect';
import {
  addCalendarEventRequest,
  addRecurringCalendarEventRequest,
  bookServiceRequestRequest,
  editCalendarEventRequest,
  rescheduleServiceRequestRequest,
} from '@/services/api/serviceRequest';

import AppointmentBookingTimeSelector from '@/components/AppointmentBooking/TimeslotSelector';
import { processTimeslots } from '@/components/AppointmentBooking/TimeslotSelector/utils';
import BranchServiceAgentCascader from '@/components/Location/BranchServiceAgentCascader';
import type { Selection } from '@/components/Location/BranchServiceAgentCascader/utils';
import Drawer from '@/components/Drawer';
import { useSubmitOnEnter } from '@/components/Form/useSubmitOnEnter';
import { fetchAgentAvailabilityRequest, fetchBranchServiceAvailabilityRequest } from '@/services/api/location';
import LeadAssigneeDropdownFilters from '@/components/Filters/LeadAssigneeDropdownFilters';
import { CustomColorPicker } from '@/components/Kanban/StateManager/LeadStateColor';

import styles from '../styles.less';
import RemoveCalendarEventAction from '@/components/Kanban/ServiceRequests/Calendar/Actions/RemoveCalendarEventAction';
import AppointmentContactField, {
  APPOINTMENT_CONTACT_FIELD,
} from '@/components/Kanban/ServiceRequests/AppointmentContactField';
import PERMISSIONS from '@/constants/permissions';
import { useHasAccess } from '@/utils/hooks';
import { usePubSub } from '@/context/PubSubContext';
import { BACKOFFICE_SERVICE_REQUEST_ACTION } from '@/context/PubSubContext/events';
import ServiceRequestItem from '@/components/Lead/ServiceRequestItem';
import { LeadActionTag } from '@/components/ActionTag';
import * as LocationSelectors from '@/selectors/locationSelectors';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import { InfoCircleOutlined, SyncOutlined } from '@ant-design/icons';
import omit from 'lodash/omit';
import { getAPIErrorMessage } from '@/utils/request';
import RecurrenceField from '@/components/Kanban/ServiceRequests/Calendar/Recurrence/RecurrenceField';
import { useRecurrenceScope } from '@/components/Kanban/ServiceRequests/Calendar/Recurrence/useRecurrenceScope';
import { describeRecurring, isRecurringEvent, toRecurringPayload } from '@/utils/recurrence';

const APPOINTMENT_TYPES = {
  SERVICE: 'service',
  CUSTOM: 'custom',
};
const TOGGLE_THRESHOLD_MS = 300;
// Mirrors the backend: `calendar_events`.`title` is varchar(100) and
// SaveCalendarEventRequest caps the edit payload at max:100.
const CALENDAR_EVENT_TITLE_MAX_LENGTH = 100;
const CreateAppointmentDrawer: React.FC = () => {
  const { initialState } = useModel('@@initialState');
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [customAppointmentTime, setCustomAppointmentTime] = useState({
    start: moment().hour(8),
    end: moment().hour(9),
  });
  const visibleAt = React.useRef();
  const [activeTab, setActiveTab] = useState(APPOINTMENT_TYPES.SERVICE);
  const [leadId, setLeadId] = useState('');
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [initialDestinationValues, setInitialDestinationValues] = useState({});
  const [availability, setAvailability] = useState([]);
  const [titleManuallyChanged, setTitleManuallyChanged] = useState(false);
  const [fetchAvailabilityLoading, setFetchAvailabilityLoading] = useState(false);
  const { publish } = usePubSub();

  const hasCalendarEventAccess = useHasAccess([PERMISSIONS.CalendarEvents.CREATE, PERMISSIONS.BUSINESS_PARTNER]);
  const hasCreateForOthers = useHasAccess([PERMISSIONS.CalendarEvents.CREATE_FOR_OTHERS, PERMISSIONS.BUSINESS_PARTNER]);
  const hasControlEmails = useHasAccess([PERMISSIONS.ServiceRequests.CONTROL_EMAILS, PERMISSIONS.BUSINESS_PARTNER]);

  // Region out of form values
  const [agent, setAgent] = useState({});
  const [lead, setLead] = useState({});
  const [currentDate, setCurrentDate] = useState(new Date());
  const [initialized, setInitialized] = useState(false);
  // endregion

  const [slotInfo, setSlotInfo] = useState<any>();

  const selectedLead = useSelector((state: DefaultRootState) => LeadSelectors.selectLeadData(state, leadId));
  const [serviceForm] = Form.useForm();
  const [customForm] = Form.useForm();

  const drawer = useSelector(ServiceRequestSelectors.selectServiceRequestDrawer);
  const allBranchesListFromStore = useSelector(LocationSelectors.selectAllBranchesList);
  // Every time shown or composed in this drawer is pinned to the user's
  // selected calendar timezone (never the browser's), so it always matches
  // the calendar grid. Submitted values keep their instant — the pinned
  // offset is serialized into the ISO string.
  const selectedTimezone = useSelector(PreferencesSelectors.selectSelectedTimezone);

  const visible = !isEmpty(omit(drawer, ['toggledAt']));
  const currentEvent = drawer?.event;
  const dispatch = useDispatch();
  const intl = useIntl();
  const { askRecurrenceScope, recurrenceScopeDrawer } = useRecurrenceScope();
  // The recurrence rule currently selected on the custom tab — when set, the
  // time selector hides its single-date box and inline calendar (the rule
  // description under the recurrence field covers the dates).
  const customRecurrence = Form.useWatch('recurrence', customForm);
  const isRecurringSelection = isEdit ? isRecurringEvent(currentEvent) : Boolean(customRecurrence);

  // Custom (calendar event) appointments only notify when linked to a lead/customer.
  // The "send email" switch's enabled state is derived from the live form values
  // inside a shouldUpdate render (see the custom tab) — the same reliable pattern
  // used to render the linked-lead tag. Form.useWatch is intentionally avoided
  // here because it misses programmatic setFieldValue updates (e.g. a freshly
  // created customer) and races with the LeadsSelect onChange.
  const customMeetingEmailPayload = (sendEmail: boolean) => ({ send_email: !!sendEmail });

  // Only control_emails holders see the service-request toggle, and only their
  // choice is sent — for everyone else the payload stays empty and the backend
  // always notifies.
  const serviceRequestEmailPayload = (sendEmail: boolean) =>
    hasControlEmails ? { send_email: !!sendEmail } : {};

  const customEventHasLead = (form): boolean =>
    !!(form.getFieldValue('preSelectedLead') || form.getFieldValue('lead'));

  const changeAssigneeConfirmationModalTexts = {
    title: intl.formatMessage({
      id: 'pages.calendar.create.changeAssigneeTitle',
      defaultMessage: 'Change assignee',
    }),
    content: intl.formatMessage({
      id: 'pages.calendar.create.changeAssigneeContent',
      defaultMessage: 'Are you sure you want to change the assignee?',
    }),
  };

  const handleTitleChange = () => setTitleManuallyChanged(true);

  const onServiceMeetingValuesChange = (changedValues, values) => {
    if (changedValues?.destination) {
      handleDestinationChange(changedValues?.destination, values.lead);
    }
  };

  useEffect(() => {
    if (initialized) return;
    if (!visible) return;
    setInitialized(true);

    if (isEmpty(allBranchesListFromStore)) {
      dispatch({
        type: 'location/getAllBranchList',
      });
    }
    dispatch({
      type: 'location/fetchFullBranchesWithDetails',
      payload: {
        pageSize: 50,
      },
    });
  }, [visible]);
  const onCustomMeetingValuesChange = (changedValues, values) => {

  };

  const clear = () => {
    setTimeout(() => {
      availabilityContextRef.current = null;
      setAgent(undefined);
      setSelectedSlot(undefined);
      setCustomAppointmentTime(undefined);
      setSlotInfo(undefined);
      setAvailability([]);
      serviceForm.resetFields();
      customForm.resetFields();
      setActiveTab(APPOINTMENT_TYPES.SERVICE);
      setIsEdit(false);
      setInitialDestinationValues({});
      setTitleManuallyChanged(false);
    }, 50);
  };

  const initiate = () => {
    visibleAt.current = Date.now();
    if (drawer.lead) {
      customForm.setFieldValue('preSelectedLead', drawer.lead);
      serviceForm.setFieldValue('preSelectedLead', drawer.lead);
      customForm.setFieldValue('lead', drawer.lead as any);
      serviceForm.setFieldValue('lead', drawer.lead as any);
    }
    if (drawer.event) {
      setIsEdit(true);
      customForm.setFieldValue('preSelectedLead', drawer.event.lead);
      // Keep the watchable `lead` field in sync so the notification toggle is
      // enabled when editing a calendar event that is already linked to a lead.
      customForm.setFieldValue('lead', drawer.event.lead as any);
      setActiveTab(drawer.event.blocker ? APPOINTMENT_TYPES.CUSTOM : APPOINTMENT_TYPES.SERVICE);
      if (!drawer.event.blocker) {
        setInitialDestinationValues({
          branchId: drawer.event?.branch_service?.branch?.id ?? drawer.event?.lead?.branch?.id,
          serviceId: drawer.event?.branch_service_id ?? drawer.event?.branch_service?.id,
          agentId: drawer.event?.assigneeId,
        });
        serviceForm.setFieldsValue({
          title: drawer.event.title,
          preSelectedLead: drawer.event.lead,
        });
      } else {
        customForm.setFieldsValue({
          title: drawer.event.title,
          assignee: drawer.event.assigneeId || drawer.event?.assignee?.id,
          color: drawer.event.color,
          leadId: drawer.event?.lead?.id,
          description: drawer.event.description,
        });
        setCustomAppointmentTime({
          start: moment(drawer.event?.utc_start_time).tz(selectedTimezone),
          end: moment(drawer.event?.utc_end_time).tz(selectedTimezone),
        });
      }
    } else if (drawer.slotInfo) {
      setSlotInfo({
        ...drawer.slotInfo,
        assigneeId: drawer.slotInfo?.resourceId,
      });

      if (drawer.slotInfo?.branchServiceId) {
        setInitialDestinationValues({
          branchId: drawer.slotInfo?.branchId,
          serviceId: drawer.slotInfo?.branchServiceId,
          agentId: drawer.slotInfo?.resourceId,
        });
        setCurrentDate(drawer.slotInfo?.start || new Date());
      } else {
        setActiveTab(APPOINTMENT_TYPES.CUSTOM);
      }

      setCustomAppointmentTime({
        start: moment(drawer.slotInfo?.start).tz(selectedTimezone),
        end: moment(drawer.slotInfo?.end).tz(selectedTimezone),
      });
      customForm.setFieldValue('assignee', drawer.slotInfo?.resourceId || initialState.currentUser?.User?.alias);

    } else {
      setCustomAppointmentTime({
        start: moment().tz(selectedTimezone).hour(8).minute(0),
        end: moment().tz(selectedTimezone).hour(9).minute(0),
      });
      customForm.setFieldValue('assignee', initialState.currentUser?.User?.alias);
    }

  };
  useEffect(() => {
    visible ? initiate() : clear();
  }, [visible]);

  const handleGetAvailability = (id, slots) => {
    slots = Array.isArray(slots) ? processTimeslots(slots, selectedTimezone) : [];
    setAvailability(slots);
    setFetchAvailabilityLoading(false);
  };

  // Remembers the last availability request so it can be replayed when the
  // selected timezone changes — the slots are pinned to a timezone at fetch
  // time (processTimeslots), so a re-fetch is what re-labels them to the new
  // zone. Also carries `tz` to the API for when the backend honors it.
  const availabilityContextRef = React.useRef<{ agentId?: string, branchServiceId: string, isWholeService?: boolean } | null>(null);

  const runAvailabilityFetch = (context: { agentId?: string, branchServiceId: string, isWholeService?: boolean }) => {
    setFetchAvailabilityLoading(true);
    const request = context.isWholeService
      ? fetchBranchServiceAvailabilityRequest({
          params: { branchServiceId: context.branchServiceId },
          query: { tz: selectedTimezone },
        })
      : fetchAgentAvailabilityRequest({
          params: { agentId: context.agentId, branchServiceId: context.branchServiceId },
          query: { tz: selectedTimezone },
        });
    request.then((response) =>
      handleGetAvailability(context.isWholeService ? context.branchServiceId : context.agentId, response.data?.data),
    );
  };

  // Re-fetch availability in the newly selected timezone. Skipped until a
  // service/agent has actually been chosen (nothing to fetch before that).
  useEffect(() => {
    if (!visible) return;
    if (!availabilityContextRef.current) return;
    runAvailabilityFetch(availabilityContextRef.current);
  }, [selectedTimezone]);

  const getForm = () => activeTab === APPOINTMENT_TYPES.SERVICE ? serviceForm : customForm;
  const handleLeadCreate = (lead) => {
    serviceForm.setFieldValue('preSelectedLead', lead);
    customForm.setFieldValue('preSelectedLead', lead);
    // `preSelectedLead` is not a registered field, so Form.useWatch never sees it.
    // Mirror the value into the registered `lead` field so derived state (e.g. the
    // notification toggle's enabled state) reacts to a freshly created customer
    // just like it does to selecting an existing one.
    serviceForm.setFieldValue('lead', lead);
    customForm.setFieldValue('lead', lead);
  };
  const handleDestinationChange = (destination = {}, lead = {}) => {
    if (isEmpty(destination)) return;

    const { agent, service, branch } = destination;
    setAgent(agent);
    setLead(lead);

    if (!lead) lead = serviceForm.getFieldValue('preSelectedLead');
    if (!branch) {
      availabilityContextRef.current = null;
      handleGetAvailability(null, []);
      setSelectedSlot(null);
      return;
    }
    if (service.service_type?.name) {
      if (!serviceForm.getFieldValue('title') || (!titleManuallyChanged && !isEdit)) {
        serviceForm.setFieldValue('title', service.service_type?.name);
      }
    } else {
      serviceForm.resetFields(['title']);
    }
    const fetchAgentAvailability = () => {
      availabilityContextRef.current = { agentId: agent.id, branchServiceId: service.id, isWholeService: false };
      runAvailabilityFetch(availabilityContextRef.current);
    };
    if (agent.isWholeService) {
      availabilityContextRef.current = { branchServiceId: service.id, isWholeService: true };
      runAvailabilityFetch(availabilityContextRef.current);
    } else {
      if (lead?.assignee?.id && (destination.agent?.id !== lead?.assignee?.id)) {
        Modal.confirm({
          ...changeAssigneeConfirmationModalTexts,
          onOk: fetchAgentAvailability,
          onCancel: () => {
            serviceForm.setFieldValue('lead', {
              ...lead,
              hydrateSelectionAt: Date.now(),
            });
          },
        });
      } else {
        fetchAgentAvailability();
      }
    }
  };

  function toggle() {
    if (visibleAt.current && (Date.now() - visibleAt.current) < TOGGLE_THRESHOLD_MS)
      return;
    dispatch({
      type: 'serviceRequest/toggleCreateServiceRequestDrawer',
    });
  }

  const resolveRequest = (({ response, data }) => {
    if (data?.error) {
      setLoading(false);
      return message.error(getAPIErrorMessage(data));
    }

    if (drawer.then) drawer.then(data);

    if ((drawer.openAppointmentDetails || isEdit) && data?.data?.id && activeTab !== APPOINTMENT_TYPES.CUSTOM) dispatch({
      type: 'serviceRequest/toggleDetailsDrawer',
      payload: {
        appointmentId: data?.data?.id,
      },
    });

    setLoading(false);
    // A recurring series responds with a RecurringResource (utc_start_time lives
    // on its first event, not the top level) — fall back to that so the calendar
    // still jumps to the series' start date.
    publish(BACKOFFICE_SERVICE_REQUEST_ACTION, {
      ...data?.data,
      utc_start_time: data?.data?.utc_start_time || data?.data?.events?.[0]?.utc_start_time,
    });
    toggle();
    dispatch({
      type: 'serviceRequest/updateFetchCalendarViewAt',
    });
  });
  const handleEdit = () => {
    if (activeTab === APPOINTMENT_TYPES.CUSTOM) {
      customForm.validateFields().then(async values => {
        const event = drawer.event;
        let apply_to;
        // Recurring series: ask whether the edit applies to this event / this and
        // following / all. The recurrence pattern itself can't be edited, only
        // title/description/color/time, so "This event" is always a valid choice.
        if (isRecurringEvent(event)) {
          const scope = await askRecurrenceScope({ action: 'edit' });
          if (!scope) return; // cancelled
          apply_to = scope;
        }
        setLoading(true);
        editCalendarEventRequest({
          params: {
            calendarEventId: event.id,
          },
          data: {
            ...omit(values, 'send_email'),
            lead: (values?.lead)?.id,
            // Never notify without a linked customer (switch disabled in that case).
            ...customMeetingEmailPayload(values?.lead ? values.send_email : false),
            start: customAppointmentTime.start.format('YYYY-MM-DDTHH:mm:ssZ'),
            end: customAppointmentTime.end.format('YYYY-MM-DDTHH:mm:ssZ'),
            // The calendar timezone the times above were entered in (falls back
            // to the browser timezone) — the backend renders notification
            // emails in it.
            timezone: selectedTimezone,
            ...(apply_to ? { apply_to } : {}),
          },
        }).then(resolveRequest);
      });

      return;
    }

    serviceForm.validateFields().then(values => {
      const newStart = selectedSlot.start.format('YYYY-MM-DDTHH:mm:ssZ');
      const newEnd = selectedSlot.end.format('YYYY-MM-DDTHH:mm:ssZ');
      // Compare the end too: an end-only edit (editable-time variants) must
      // also go through the reschedule endpoint — the request helper strips
      // start/end entirely when timeChanged is false.
      const timeChanged = newStart !== moment(currentEvent?.utc_start_time).format('YYYY-MM-DDTHH:mm:ssZ')
        || newEnd !== moment(currentEvent?.utc_end_time).format('YYYY-MM-DDTHH:mm:ssZ');
      const payload = {
        start: selectedSlot.start.format('YYYY-MM-DDTHH:mm:ssZ'),
        end: selectedSlot.end.format('YYYY-MM-DDTHH:mm:ssZ'),
        title: values.title,
        branch_service: values?.destination?.service?.id,
        ...serviceRequestEmailPayload(values?.send_email),
      };
      if (!agent.isWholeService) {
        payload.assignee = agent.id;
      }
      setLoading(true);
      rescheduleServiceRequestRequest({
        params: { serviceRequestId: drawer.event.id },
        data: payload,
        options: {
          timeChanged,
        },
      }).then(resolveRequest);

    });

  };
  const handleSubmit = () => {
    if (isEdit) return handleEdit();
    if (activeTab === APPOINTMENT_TYPES.SERVICE) {
      serviceForm.validateFields().then(values => {
        setLoading(true);
        const preSelectedLead = serviceForm.getFieldValue('preSelectedLead');
        const payload = {
          start: selectedSlot.start.format('YYYY-MM-DDTHH:mm:ssZ'),
          end: selectedSlot.end.format('YYYY-MM-DDTHH:mm:ssZ'),
          branch_service: values?.destination?.service?.id,
          title: values?.title,
          ...serviceRequestEmailPayload(values?.send_email),
          // B2B: who it's with among the project's contacts; left out, the backend takes the project's primary.
          ...(values?.[APPOINTMENT_CONTACT_FIELD] ? { contact_id: values[APPOINTMENT_CONTACT_FIELD] } : {}),
        };
        if (!agent.isWholeService) {
          payload.assignee = agent.id;
        }
        bookServiceRequestRequest({
          params: {
            leadId: (preSelectedLead || values?.lead)?.id,
          },
          data: payload,
        }).then(resolveRequest);
      });
      return;
    }

    if (activeTab === APPOINTMENT_TYPES.CUSTOM) {
      customForm.validateFields().then(values => {
        setLoading(true);
        const preSelectedLead = customForm.getFieldValue('preSelectedLead');
        const base = {
          assignee: values.assignee,
          color: values.color,
          lead: (preSelectedLead || values?.lead)?.id,
          title: values.title,
          description: values.description,
        };
        const start = customAppointmentTime.start.format('YYYY-MM-DDTHH:mm:ssZ');
        const end = customAppointmentTime.end.format('YYYY-MM-DDTHH:mm:ssZ');

        // A recurrence rule creates the whole series in one call — there is no
        // "recurrence" field on the plain single-event endpoint.
        if (values.recurrence) {
          addRecurringCalendarEventRequest({
            data: {
              ...base,
              start,
              end,
              // Never notify without a linked customer (the switch is disabled in
              // that case; guard against a stale "on" value after a lead removal).
              ...customMeetingEmailPayload((preSelectedLead || values?.lead) ? values.send_email : false),
              ...toRecurringPayload(values.recurrence, customAppointmentTime.start, selectedTimezone),
            },
          }).then(resolveRequest);
          return;
        }

        addCalendarEventRequest({
          data: {
            ...base,
            start,
            end,
            // Never notify without a linked customer (the switch is disabled in
            // that case; guard against a stale "on" value after a lead removal).
            ...customMeetingEmailPayload((preSelectedLead || values?.lead) ? values.send_email : false),
            // The calendar timezone the times above were entered in (falls back
            // to the browser timezone) — the backend renders notification
            // emails in it. Recurring series carry it via toRecurringPayload.
            timezone: selectedTimezone,
          },
        }).then(resolveRequest);
      });
    }
  };

  const screens = Grid.useBreakpoint();

  const isDisabled = () => {
    if (activeTab === APPOINTMENT_TYPES.SERVICE) return isEmpty(selectedSlot) || isEmpty(agent);
    if (activeTab === APPOINTMENT_TYPES.CUSTOM) return isEmpty(customAppointmentTime?.start) || isEmpty(customAppointmentTime?.end);
    return true;
  };

  const getLeadDefaultFilters = (agentId) => {
    // Commented Aug 2025 since all leads should be searchable
    // if (agentId || (drawer?.slotInfo && drawer?.slotInfo?.resourceId && !slotInfo?.noLeadFilter)) {
    //   return {
    //     assignee: `${agentId || drawer?.slotInfo?.resourceId},null`,
    //   };
    // }
    return {};
  };

  const getCreateLeadPayload = (agentId, destination?: Selection) => {
    const payload: Record<string, any> = {};
    if (agentId || (slotInfo && slotInfo.resourceId)) {
      payload.assignee = agentId || slotInfo?.resourceId;
    }
    if (destination?.service?.id) {
      payload.branchServiceId = destination.service.id;
      payload.branchId = destination.branch?.id;
    }
    return payload;
  };

  const handleResetDefaultAgentFilter = () => {
    setSlotInfo({ ...slotInfo, noLeadFilter: true });
  };

  const getTitle = () => {
    if (isEdit) {
      if (activeTab === APPOINTMENT_TYPES.SERVICE) {
        return intl.formatMessage({
          id: 'pages.serviceRequests.rescheduleAppointment',
          defaultMessage: 'Reschedule Appointment',
        });
      }
      return `${intl.formatMessage({
        id: 'common.actions.edit',
        defaultMessage: 'Edit',
      })} - ${drawer?.event?.title}`;
    }
    return intl.formatMessage({
      id: 'pages.serviceRequests.bookAppointment',
      defaultMessage: 'Book Appointment',
    });
  };
  const getSubmitButtonText = () => {
    if (isEdit) {
      if (activeTab === APPOINTMENT_TYPES.SERVICE) {
        return intl.formatMessage({
          id: 'pages.serviceRequests.reschedule',
          defaultMessage: 'Reschedule',
        });
      }
      return intl.formatMessage({
        id: 'common.actions.save',
        defaultMessage: 'Save',
      });
    }
    return intl.formatMessage({
      id: 'common.actions.create',
      defaultMessage: 'Create',
    });
  };
  const getRemoveAction = () => {
    if (isEdit && activeTab === APPOINTMENT_TYPES.CUSTOM) {
      return <RemoveCalendarEventAction
        buttonSize={'large'}
        calendarEvent={drawer?.event} setLoading={setLoading} onSuccess={resolveRequest} />;
    }
    return null;
  };

  const submitOnEnter = useSubmitOnEnter(handleSubmit, {
    enabled: !loading && !isDisabled(),
  });

  function getFooter() {
    return (
      <div style={{ textAlign: 'right' }}>
        <Space>
          {getRemoveAction()}
          <Button
            className={'button-secondary button-bordered'}
            size={'large'}
            type={'primary'}
            onClick={toggle}
            data-testid="create-service-request-close-btn"
          >
            <F id={'common.actions.close'} defaultMessage={'Close'} />
          </Button>
          <Button loading={loading} disabled={isDisabled()} size={'large'}
                  onClick={handleSubmit} type={'primary'}
                  data-testid="create-service-request-submit-btn">
            {getSubmitButtonText()}
          </Button>
        </Space>
      </div>
    );
  }

  const getLeadFormItem = form => {
    const preSelectedLead = form.getFieldValue('preSelectedLead');

    const getContent = () => {
      if (!visible) return null;
      if (preSelectedLead) return <div className={styles.preSelectedLead}>
        <LeadActionTag size={'large'} lead={preSelectedLead} />
        {/*Remove reset button as suggested by sepehr*/}
        {/*{!isEdit && <div>*/}
        {/*  <Button size={'small'} onClick={() => {*/}
        {/*    serviceForm.resetFields(['preSelectedLead']);*/}
        {/*    customForm.resetFields(['preSelectedLead']);*/}

        {/*  }}>*/}
        {/*    <F id={'common.actions.clear'} defaultMessage={'Clear'} />*/}
        {/*  </Button>*/}
        {/*</div>}*/}
      </div>;

      const agentId = activeTab === APPOINTMENT_TYPES.SERVICE ? undefined : form.getFieldValue('assignee');

      return <LeadsSelect filterMode={'select'}
                          getFullObject
                          hasCreateLead
                          showEmail
                          groupByCustomer
                          createLeadPayload={getCreateLeadPayload(agentId, form.getFieldValue('destination'))}
                          onCreate={handleLeadCreate}
                          initialQuery={getLeadDefaultFilters(agentId)}
                          selectProps={{
                            width: '100%',
                            style: {
                              display: 'flex',
                            },
                            size: 'large',
                          }}
      />;
    };

    const isRequired = activeTab === APPOINTMENT_TYPES.SERVICE && !preSelectedLead;
    return (
      <ProForm.Item
        required={isRequired}
        rules={isRequired ? [{ required: true }] : []}
        label={
          <F
            id={'pages.leads.customer_project_label'}
            defaultMessage={'Customer / Project'}
          />
        }
        name={'lead'}
        style={{ marginBottom: '0px !important' }}
      >
        {getContent()}
      </ProForm.Item>
    );
  };

  const tabs = [
    {
      key: APPOINTMENT_TYPES.SERVICE,
      label: (
        <span data-testid="tab-service">
          <F
            id={'pages.calendar.appointmentType.service'}
            defaultMessage={'Service'}
          />
        </span>
      ),
      children: (
        <div className={styles.appointmentFormContainer}>
          <div className={styles.appointmentFormData}>
            {drawer?.event && (
              <div>
                <ServiceRequestItem
                  serviceRequest={drawer.event}
                  lead={drawer.lead}
                  hideActions
                  hideExtraFields
                  hideMigrationSource
                />
                <br />
              </div>
            )}

            <ProForm
              {...submitOnEnter}
              form={serviceForm}
              submitter={false}
              initialValues={{}}
              onValuesChange={onServiceMeetingValuesChange}
            >
              <ProFormText
                fieldProps={{
                  size: 'large',
                  onChange: handleTitleChange,
                  // @ts-ignore
                  'data-testid': 'service-appointment-title'
                }}
                label={intl.formatMessage({
                  id: 'common.attribute.title',
                  defaultMessage: 'Title',
                })}
                name={'title'}
              />

              <ProForm.Item shouldUpdate style={{ margin: 0 }}>
                {(form) => getLeadFormItem(form)}
              </ProForm.Item>
              {/* Rescheduling keeps the appointment's contact. */}
              {!isEdit && (
                <ProForm.Item shouldUpdate noStyle>
                  {(form) => {
                    const lead = form.getFieldValue('preSelectedLead') || form.getFieldValue('lead');
                    return lead?.id ? <AppointmentContactField key={lead.id} leadId={lead.id} /> : null;
                  }}
                </ProForm.Item>
              )}
              <ProForm.Item shouldUpdate>
                {(form) => {
                  const lead =
                    form.getFieldValue('preSelectedLead') ||
                    form.getFieldValue('lead');
                  return (
                    <ProForm.Item
                      label={intl.formatMessage({
                        id: 'pages.calendar.cascader.serviceOrAgent',
                        defaultMessage: 'Service / Agent',
                      })}
                      name={'destination'}
                    >
                      {/* @ts-ignore */}
                      <BranchServiceAgentCascader
                        initialValue={initialDestinationValues}
                        lead={lead}
                        // disabled={isEmpty(lead) && isEmpty(initialDestinationValues)}
                      />
                    </ProForm.Item>
                  );
                }}
              </ProForm.Item>
              {hasControlEmails && (
                <ProForm.Item
                  name={'send_email'}
                  initialValue={true}
                  valuePropName={'checked'}
                >
                  <ToggleCard
                    title={intl.formatMessage({
                      id: 'pages.calendar.sendEmail.label',
                      defaultMessage: 'Send notification email',
                    })}
                    description={intl.formatMessage({
                      id: 'pages.calendar.sendEmail.tooltip',
                      defaultMessage:
                        'When enabled, the customer and the agent receive an email about this appointment.',
                    })}
                  />
                </ProForm.Item>
              )}
            </ProForm>
          </div>
          <div className={classNames(styles.appointmentFormTime)}>
            <Spin spinning={fetchAvailabilityLoading}>
              <div
                className={classNames(styles.timeSelectorWrapper, {
                  [styles.blurred]: availability.length === 0 || isEmpty(agent),
                })}
              >
                <AppointmentBookingTimeSelector
                  onSelect={setSelectedSlot}
                  date={currentDate}
                  onDateChange={setCurrentDate}
                  selectedSlot={selectedSlot}
                  showPlaceHolder
                  showTimezone
                  timeslots={availability}
                />
              </div>
            </Spin>
            {!isEmpty(agent) &&
              availability.length === 0 &&
              !fetchAvailabilityLoading && (
                <div data-testid="service-not-available" className={styles.notAvailable}>
                  <Empty
                    description={intl.formatMessage({
                      id: 'pages.calendar.agentNotAvailable',
                      defaultMessage:
                        'Selected service/agent is not available. Please try selecting another agent.',
                    })}
                  />
                </div>
              )}
          </div>
        </div>
      ),
    },
    hasCalendarEventAccess && {
      key: APPOINTMENT_TYPES.CUSTOM,
      label: (
        <span data-testid="tab-custom">
          <F
            id={'pages.calendar.appointmentType.custom'}
            defaultMessage={'Custom'}
          />
        </span>
      ),
      children: (
        <ProForm
          {...submitOnEnter}
          form={customForm}
          submitter={false}
          initialValues={{
            color: '#92a8d1',
          }}
          onValuesChange={onCustomMeetingValuesChange}
        >
          <div className={styles.appointmentFormContainer}>
            <div className={styles.appointmentFormData}>
              <ProFormText
                rules={[{ required: true }]}
                fieldProps={{
                  size: 'large',
                  maxLength: CALENDAR_EVENT_TITLE_MAX_LENGTH,
                  showCount: true,
                  // @ts-ignore
                  'data-testid': 'custom-appointment-title'
                }}
                label={'Title'}
                name={'title'}
              />
              {hasCreateForOthers ? (
                <ProForm.Item
                  rules={[{ required: true }]}
                  label={intl.formatMessage({
                    id: 'pages.calendar.custom.agent',
                    defaultMessage: 'Agent',
                  })}
                  name={'assignee'}
                >
                  <LeadAssigneeDropdownFilters
                    type="dropdown"
                    defaultMe={
                      !slotInfo?.assigneeId && !drawer?.event?.assigneeId
                    }
                    defaultValue={
                      slotInfo?.assigneeId || drawer?.event?.assigneeId
                    }
                    selectProps={{
                      size: 'large',
                    }}
                    filterMode={'select'}
                  />
                </ProForm.Item>
              ) : (
                <ProForm.Item name={'assignee'} noStyle>
                  <Input type="hidden" />
                </ProForm.Item>
              )}
              <ProForm.Item
                name={'color'}
                label={intl.formatMessage({
                  id: 'pages.calendar.custom.color',
                  defaultMessage: 'Color',
                })}
                initialValue={'#7fcdcd'}
              >
                <CustomColorPicker />
              </ProForm.Item>
              <ProForm.Item style={{ margin: 0 }} shouldUpdate>
                {(form) => getLeadFormItem(form)}
              </ProForm.Item>
              <ProFormTextArea
                fieldProps={{ size: 'large' }}
                label={intl.formatMessage({
                  id: 'common.attribute.description',
                  defaultMessage: 'Description',
                })}
                name={'description'}
              />
              {/* Calendar events are not gated by control_emails — anyone can pick
                  whether to notify the linked customer. Defaults to off and is only
                  enabled once a lead/customer is linked. The enabled state is read
                  from the live form values (shouldUpdate) so it reacts to a freshly
                  created customer as reliably as the lead tag above. */}
              <ProForm.Item noStyle shouldUpdate>
                {(form) => {
                  const hasLead = customEventHasLead(form);
                  return (
                    <ProForm.Item
                      name={'send_email'}
                      initialValue={false}
                      valuePropName={'checked'}
                      className={hasLead ? undefined : styles.disabledNotificationSwitch}
                    >
                      <ToggleCard
                        disabled={!hasLead}
                        title={intl.formatMessage({
                          id: 'pages.calendar.sendEmail.label',
                          defaultMessage: 'Send notification email',
                        })}
                        description={intl.formatMessage(
                          hasLead
                            ? {
                                id: 'pages.calendar.sendEmail.custom_hint',
                                defaultMessage:
                                  'Only sent when the meeting is linked to a lead. The linked customer receives the invitation.',
                              }
                            : {
                                id: 'pages.calendar.sendEmail.requiresLead',
                                defaultMessage:
                                  'Connect a customer / project to enable email notifications.',
                              },
                        )}
                      />
                    </ProForm.Item>
                  );
                }}
              </ProForm.Item>
            </div>
            <div className={styles.appointmentFormTime}>
              {/*@ts-ignore*/}
              <AppointmentBookingTimeSelector
                flexibleTime
                showTimezone
                onDateChange={setCurrentDate}
                date={currentDate}
                onSelect={setCustomAppointmentTime}
                selectedSlot={customAppointmentTime}
                recurring={isRecurringSelection}
                extra={
                  isEdit ? (
                    isRecurringEvent(drawer?.event) && (
                      <div className={styles.recurrenceField}>
                        <Space direction={'vertical'} size={4}>
                          {drawer?.event?.recurring && (
                            <Typography.Text>
                              <SyncOutlined style={{ marginRight: 8 }} />
                              {describeRecurring(drawer.event.recurring, intl)}
                            </Typography.Text>
                          )}
                          <Typography.Text type={'secondary'}>
                            <small>
                              <InfoCircleOutlined style={{ marginRight: 8 }} />
                              <F
                                id="pages.calendar.recurrence.partOfSeries"
                                defaultMessage="Part of a recurring series. The recurrence pattern can't be changed after creation."
                              />
                            </small>
                          </Typography.Text>
                        </Space>
                      </div>
                    )
                  ) : (
                    <ProForm.Item
                      className={styles.recurrenceField}
                      name={'recurrence'}
                    >
                      <RecurrenceField start={customAppointmentTime?.start} />
                    </ProForm.Item>
                  )
                }
              />
            </div>
          </div>
        </ProForm>
      ),
    },
  ].filter(Boolean);
  const getWidth = () => activeTab === APPOINTMENT_TYPES.CUSTOM ? 800 : 1000;

  return (
    <div className={styles.drawerContainer}>
      <Drawer
        data-testid="create-service-request-drawer"
        width={getWidth()}
        footer={getFooter()}
        title={getTitle()}
        open={visible}
        onOpenChange={toggle}
      >
        <Tabs
          data-testid="create-service-request-tabs"
          destroyOnHidden={false}
          size={'large'}
          className={classNames(
            'ant-tab-type-button',
            styles.leadDetailsDrawerTabs,
            {
              [styles.hideTabs]: isEdit || !hasCalendarEventAccess,
            },
          )}
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabs}
        />
      </Drawer>
      {recurrenceScopeDrawer}
    </div>
  );
};

export default CreateAppointmentDrawer;
